"use client"

import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import Countdown from "react-countdown"
import { Mail, Lock, KeyRound, ArrowLeft, RefreshCw, Leaf, Eye, EyeOff, ArrowRight, Clock, AlertCircle } from "lucide-react"

// Import your existing utilities and components
import APIRequests from "../../../utility/APIRequests"
import { notify } from "../../../utility/Helper1"
import { allRemoteSettingsSelect } from "../../../redux/selectors/index"
import ReCAPTCHA from "../../../utility/customComponents/ReCAPTCHA";
import AuthHolder from "./AuthHolder";

const ResetPassword = () => {
    const [emailInput, setEmailInput] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmNewPassword, setConfirmNewPassword] = useState("")
    const [emailCode, setEmailCode] = useState("")
    const [requestOTPRetObj, setRequestOTPRetObj] = useState({})
    const [uiLevel, setUiLevel] = useState(0)
    const [fullPageLoading, setFullPageLoading] = useState(false)
    const [loadingText, setLoadingText] = useState("")
    const [loading, changeLoading] = useState(false)
    const [captcha, setCaptcha] = useState(null)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [focusedField, setFocusedField] = useState('')

    const navigate = useNavigate()
    const site_settings = useSelector(allRemoteSettingsSelect)

    const captchaHasChanged = (data) => {
        setCaptcha(data)
    }

    const requestOTP = async (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        setFullPageLoading(true)
        changeLoading(true)
        setLoadingText("Sending reset code...")

        const loadApi = new APIRequests()
        const getPost = await loadApi.runPost("account/reset_password_pin", { email: emailInput })

        if (getPost?.code == 1) {
            setUiLevel(1)
            setRequestOTPRetObj(getPost?.data)
        }

        changeLoading(false)
        notify(getPost?.msg, getPost?.code)
        setFullPageLoading(false)
    }

    const changePassword = async (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        if (!captcha && Number(site_settings?.site_settings?.use_google_reCaptcha) == 1) {
            notify("Invalid captcha response", 0)
            return
        }

        if (newPassword != confirmNewPassword || newPassword == "") {
            notify("Passwords must match", 0)
            return
        }

        changeLoading(true)
        setLoadingText("Updating password...")
        setFullPageLoading(true)

        const loadApi = new APIRequests()
        const getPost = await loadApi.runPost("account/reset_password", {
            captcha,
            email: emailInput,
            password: newPassword,
            passwordResetCode: emailCode,
        })

        if (getPost?.code == 1) {
            setUiLevel(0)
            notify("Password reset successful. You are being redirected to login page", 1)

            setTimeout(() => {
                navigate("/signin", { replace: true })
            }, 3000)
        } else {
            notify(getPost?.msg ?? "error", getPost?.code ?? 0)
        }

        changeLoading(false)
        setLoadingText("")
        setFullPageLoading(false)
    }

    const handleReload = () => {
        window.location.reload()
    }

    return (
        <AuthHolder skipHeader={true} title="Reset Password">
            {/* Full page loading overlay */}
            {fullPageLoading && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-gray-900/90 rounded-2xl p-8 border border-amber-500/20">
                        <div className="flex items-center space-x-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent"></div>
                            <span className="text-white font-medium">{loadingText}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 1: Email Input */}
            {uiLevel === 0 && (
                <form onSubmit={requestOTP} className="space-y-6">
                    {/* Header for Step 1 */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-amber-400/20 to-yellow-500/20 rounded-2xl mb-4 border border-amber-400/30">
                            <Mail className="text-amber-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Reset Your Password</h3>
                        <p className="text-gray-400">
                            Enter your email address to receive a reset code
                        </p>
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                        <div className="relative">
                            <input
                                type="email"
                                placeholder="Enter your email address"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onFocus={() => setFocusedField('email')}
                                onBlur={() => setFocusedField('')}
                                required
                                className="w-full h-14 pl-6 pr-6 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                            />
                            <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                                focusedField === 'email' || emailInput ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                            }`}>
                                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-14 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 hover:from-amber-500 hover:via-yellow-600 hover:to-amber-700 text-black font-semibold rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group"
                    >
                        <div className="flex items-center justify-center relative z-10">
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-3"></div>
                                    Sending Reset Code...
                                </>
                            ) : (
                                <>
                                    Send Reset Code
                                    <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
                                </>
                            )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                    </button>

                    {/* Back to Sign In */}
                    <div className="text-center pt-4">
                        <Link
                            to="/signin"
                            className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors text-sm"
                        >
                            <ArrowLeft className="mr-2" size={16} />
                            Back to Sign In
                        </Link>
                    </div>
                </form>
            )}

            {/* Step 2: Password Reset */}
            {uiLevel === 1 && (
                <form onSubmit={changePassword} className="space-y-6">
                    {/* Header for Step 2 */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-amber-400/20 to-yellow-500/20 rounded-2xl mb-4 border border-amber-400/30">
                            <Lock className="text-amber-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Create New Password</h3>
                        <p className="text-gray-400">
                            Enter the code from your email and set a new password
                        </p>
                    </div>

                    {/* Reset Code Field */}
                    <div className="space-y-2">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Enter reset code from email"
                                value={emailCode}
                                onChange={(e) => setEmailCode(e.target.value)}
                                onFocus={() => setFocusedField('code')}
                                onBlur={() => setFocusedField('')}
                                required
                                className="w-full h-14 pl-6 pr-6 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                            />
                            <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                                focusedField === 'code' || emailCode ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                            }`}>
                                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* Countdown Timer */}
                    <div className="bg-gray-800/30 border border-amber-500/20 rounded-2xl p-4">
                        <div className="flex items-center space-x-3">
                            <Clock className="text-amber-400" size={20} />
                            <div className="text-sm text-gray-300">
                                <Countdown
                                    zeroPadTime={2}
                                    key={new Date(requestOTPRetObj?.passwordResetCodeTimer)}
                                    date={new Date(requestOTPRetObj?.passwordResetCodeTimer)}
                                    renderer={({ hours, minutes, seconds, completed }) => {
                                        if (completed) {
                                            return (
                                                <span className="flex items-center text-red-400">
                                                    <AlertCircle className="mr-2" size={16} />
                                                    Code expired.{" "}
                                                    <button
                                                        onClick={handleReload}
                                                        className="ml-2 text-amber-400 hover:text-amber-300 font-medium flex items-center"
                                                    >
                                                        <RefreshCw className="h-4 w-4 mr-1" /> Try again
                                                    </button>
                                                </span>
                                            )
                                        } else {
                                            return (
                                                <span className="text-amber-400">
                                                    Code expires in{" "}
                                                    <span className="font-bold">
                                                        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                                                    </span>
                                                </span>
                                            )
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* New Password Field */}
                    <div className="space-y-2">
                        <div className="relative">
                            <input
                                type={showNewPassword ? 'text' : 'password'}
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                onFocus={() => setFocusedField('newPassword')}
                                onBlur={() => setFocusedField('')}
                                required
                                className="w-full h-14 pl-6 pr-14 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                            >
                                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                            <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                                focusedField === 'newPassword' || newPassword ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                            }`}>
                                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="space-y-2">
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Confirm new password"
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                onFocus={() => setFocusedField('confirmPassword')}
                                onBlur={() => setFocusedField('')}
                                required
                                className="w-full h-14 pl-6 pr-14 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                            >
                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                            <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                                focusedField === 'confirmPassword' || confirmNewPassword ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                            }`}>
                                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* reCAPTCHA - Conditional rendering based on site settings */}
                    {site_settings?.site_settings?.use_google_reCaptcha == 1 && (
                        <div className="pt-2">
                            <ReCAPTCHA onChange={captchaHasChanged} />
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-14 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 hover:from-amber-500 hover:via-yellow-600 hover:to-amber-700 text-black font-semibold rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group"
                    >
                        <div className="flex items-center justify-center relative z-10">
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-3"></div>
                                    {loadingText}
                                </>
                            ) : (
                                <>
                                    Update Password
                                    <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
                                </>
                            )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                    </button>

                    {/* Back to Sign In */}
                    <div className="text-center pt-4">
                        <Link
                            to="/signin"
                            className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors text-sm"
                        >
                            <ArrowLeft className="mr-2" size={16} />
                            Back to Sign In
                        </Link>
                    </div>
                </form>
            )}
        </AuthHolder>
    )
}

export default ResetPassword